import type { ReactNode } from 'react';
import type { Visibility } from '@/types/api';
import { VISIBILITY } from '@/lib/copy';
import { cn } from '@/lib/utils';

/* ---------------------------------------------------------------------------
   THE VISIBILITY BAND — this dashboard's signature device.

   The expert reads THE SAME THREAD the client reads, and prompts the agent
   inside it. Those turns are never visible to the client (invariant I7). That
   is not a detail of this screen; it is the screen's whole reason to exist, and
   getting it wrong in either direction is the worst thing this app can do — a
   reviewer who forgets their turns are hidden writes a request-changes note
   that references a conversation the client never saw.

   So visibility is drawn as STRUCTURE, not as a badge.

   Every hidden turn carries a second rail: a 3px diagonal-hatched band down its
   full height, in a hue used for nothing else, with a mono stamp at its head.
   Client-visible turns sit clean against the ground with no band at all. The
   result is that an expert scrolling a long thread reads which half of the
   conversation is hidden from the peripheral texture alone, without stopping to
   read anything — and the two halves stay distinguishable in a screenshot, on a
   bad monitor, and for a colour-blind reviewer, because the difference is a
   pattern and a position rather than only a colour.

   It is the Run Ledger rail given a second track, which is exactly what an
   expert's thread is: one thread, two visibilities.
--------------------------------------------------------------------------- */

export function VisibilityBand({
  visibility,
  children,
  className,
  /** Suppresses the head stamp for a run of turns that share one band. */
  stamp = true,
}: {
  visibility: Visibility;
  children: ReactNode;
  className?: string;
  stamp?: boolean;
}) {
  const hiddenFromClient = visibility !== 'client';
  const copy = VISIBILITY[visibility];

  if (!hiddenFromClient) {
    return <div className={cn('relative', className)}>{children}</div>;
  }

  return (
    <div className={cn('relative pl-3', className)} data-visibility={visibility}>
      {/* The band. Hatched rather than filled: a fill at this width reads as a
          coloured border, and every other coloured border in the console means
          a state rather than an audience. */}
      <span
        aria-hidden
        className="hatch-hidden pointer-events-none absolute bottom-0 left-0 top-0 w-[3px] rounded-[1px]"
      />
      {stamp ? (
        <p className="mb-1 flex items-center gap-1.5 font-mono text-tiny uppercase tracking-[0.1em] text-hidden">
          <HiddenGlyph />
          {copy.label}
        </p>
      ) : (
        <span className="sr-only">{copy.label}</span>
      )}
      {children}
    </div>
  );
}

/** A struck-through eye. Shape, so the marker survives the colour going away. */
export function HiddenGlyph({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 12 12"
      className={cn('h-[11px] w-[11px] shrink-0 stroke-current', className)}
      fill="none"
      strokeWidth="1.1"
    >
      <path d="M1 6s2-3 5-3 5 3 5 3-2 3-5 3-5-3-5-3Z" />
      <circle cx="6" cy="6" r="1.3" />
      <path d="M1.6 10.4 10.4 1.6" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The header readout: the true status beside what the client is being shown.
 *
 * This pair is on screen at all times during a review because the projection is
 * the second half of I7 — the client's status is pinned across the whole review
 * pipeline so they cannot infer the existence or the timing of the hidden turns
 * from a flickering status. An expert writing a note to the client needs to know
 * what the client currently believes is happening, and no other control on the
 * page carries that.
 */
export function VisibilitySplit({
  yours,
  theirs,
  note,
  className,
}: {
  yours: ReactNode;
  theirs: ReactNode;
  note?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1', className)} title={note}>
      <span className="flex items-center gap-1.5">
        <span className="eyebrow">You see</span>
        {yours}
      </span>
      <span aria-hidden className="h-3 w-px bg-rule-strong" />
      <span className="flex items-center gap-1.5">
        <span className="eyebrow flex items-center gap-1 text-hidden">
          <HiddenGlyph className="h-[9px] w-[9px]" />
          Client sees
        </span>
        {theirs}
      </span>
    </div>
  );
}
